"use client";

import React, { useCallback, useId, useRef, useState } from "react";
import { Upload, X, File, Eye } from "lucide-react";
import { toast } from "sonner";
import { Typography } from "@/components/common/ui/Typography";
import { cn } from "@/lib/utils/cn";

const isFileTypeAccepted = (file: File, accept: string): boolean => {
  const tokens = accept
    .split(",")
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
  if (tokens.length === 0) return true;

  const name = file.name.toLowerCase();
  const mime = file.type.toLowerCase();

  return tokens.some((token) => {
    if (token.startsWith(".")) return name.endsWith(token);
    if (token.endsWith("/*")) return mime.startsWith(token.slice(0, -1));
    return mime === token;
  });
};

export interface FileUploadProps {
  onFilesChange?: (files: File[]) => void;
  acceptedFileTypes?: string;
  maxFiles?: number;
  maxFileSize?: number; // in MB
  label?: string;
  helperText?: string;
  error?: string;
  className?: string;
  size?: "sm" | "md";
  onPreviewFile?: (file: File) => void;
}

export interface UploadedFile {
  id: string;
  file: File;
}

export function FileUpload({
  onFilesChange,
  acceptedFileTypes = ".pdf,.doc,.docx",
  maxFiles = 10,
  maxFileSize = 10,
  label,
  helperText,
  error,
  className = "",
  size = "md",
  onPreviewFile,
}: FileUploadProps) {
  const compact = size === "sm";
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = useId();

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files) return;

      const fileArray = Array.from(files);
      const validFiles: UploadedFile[] = [];

      fileArray.forEach((file) => {
        if (!isFileTypeAccepted(file, acceptedFileTypes)) {
          toast.error(`${file.name}: unsupported format. Allowed: ${acceptedFileTypes}`);
          return;
        }

        if (file.size > maxFileSize * 1024 * 1024) {
          toast.error(`File ${file.name} exceeds maximum size of ${maxFileSize}MB`);
          return;
        }

        if (uploadedFiles.length + validFiles.length >= maxFiles) {
          toast.error(`Maximum ${maxFiles} files allowed`);
          return;
        }

        validFiles.push({
          id: `${Date.now()}-${Math.random()}`,
          file,
        });
      });

      const newFiles = [...uploadedFiles, ...validFiles];
      setUploadedFiles(newFiles);
      onFilesChange?.(newFiles.map((f) => f.file));
    },
    [uploadedFiles, acceptedFileTypes, maxFileSize, maxFiles, onFilesChange]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const removeFile = useCallback(
    (id: string) => {
      const newFiles = uploadedFiles.filter((f) => f.id !== id);
      setUploadedFiles(newFiles);
      onFilesChange?.(newFiles.map((f) => f.file));
    },
    [uploadedFiles, onFilesChange]
  );

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  return (
    <div className={cn("w-full", className)}>
      {label && (
        <Typography as="label" variant="body" weight="medium" color="text-navigator-text-primary" className="block mb-2">
          {label}
        </Typography>
      )}

      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={cn(
          "border-2 border-dashed rounded-xl text-center transition-all duration-200",
          compact ? "p-6" : "p-8",
          isDragging
            ? "border-navigator-primary bg-navigator-accent-light"
            : error
            ? "border-red-300 bg-red-50/30"
            : "border-gray-200 bg-gray-50/50 hover:border-navigator-primary/40 hover:bg-navigator-accent-light/30",
          "cursor-pointer"
        )}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={acceptedFileTypes}
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = "";
          }}
          className="hidden"
          id={inputId}
        />
        <label
          htmlFor={inputId}
          className={cn("cursor-pointer flex flex-col items-center", compact ? "gap-2" : "gap-3")}
        >
          <div className={cn("bg-navigator-primary/10 rounded-full flex items-center justify-center", compact ? "w-9 h-9" : "w-12 h-12")}>
            <Upload className={cn("text-navigator-primary", compact ? "w-5 h-5" : "w-6 h-6")} />
          </div>
          <div>
            <Typography as="p" variant={compact ? "small" : "headline5"} weight="semibold" color="text-navigator-text-primary" className="mb-1">
              Drag and drop files here
            </Typography>
            {helperText && (
              <Typography as="p" variant="body" color="light">{helperText}</Typography>
            )}
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              inputRef.current?.click();
            }}
            className={cn(
              "bg-navigator-primary text-white rounded-lg font-medium cursor-pointer hover:bg-navigator-primary-dark transition-colors",
              compact ? "px-3 py-1.5 text-sm" : "px-4 py-2",
            )}
          >
            Browse Files
          </button>
        </label>
      </div>

      {error && <Typography as="p" variant="body" color="error" className="mt-1.5">{error}</Typography>}

      {uploadedFiles.length > 0 && (
        <div className="mt-4 space-y-2">
          {uploadedFiles.map((uploadedFile) => (
            <div
              key={uploadedFile.id}
              onClick={onPreviewFile ? () => onPreviewFile(uploadedFile.file) : undefined}
              role={onPreviewFile ? "button" : undefined}
              className={cn(
                "flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg hover:border-navigator-primary/40 transition-colors",
                onPreviewFile && "cursor-pointer",
              )}
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-8 h-8 bg-navigator-accent-light rounded-lg flex items-center justify-center shrink-0">
                  <File className="w-4 h-4 text-navigator-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <Typography as="p" variant="body" weight="medium" color="text-navigator-text-primary" truncate>
                    {uploadedFile.file.name}
                  </Typography>
                  <Typography as="p" variant="small" color="light">
                    {formatFileSize(uploadedFile.file.size)}
                  </Typography>
                </div>
              </div>
              <div className="ml-3 flex items-center gap-1 shrink-0">
                {onPreviewFile && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onPreviewFile(uploadedFile.file);
                    }}
                    className="p-1.5 hover:bg-navigator-accent-light rounded-lg transition-colors"
                    type="button"
                    aria-label={`Preview ${uploadedFile.file.name}`}
                  >
                    <Eye className="w-4 h-4 text-gray-400 hover:text-navigator-primary" />
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFile(uploadedFile.id);
                  }}
                  className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                  type="button"
                  aria-label={`Remove ${uploadedFile.file.name}`}
                >
                  <X className="w-4 h-4 text-gray-400 hover:text-red-600" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
